<script>
import {
  CdrText, CdrList, CdrLink, IconExternalLink, CdrChip, IconHeartStroke,
} from '@rei/cedar';
import hoursMixin from '../../mixins/storeCommons';
import metrics from '../../mixins/metrics';

export default {
  name: 'StoreDataComponent',
  components: {
    CdrText,
    CdrLink,
    CdrList,
    CdrChip,
    IconExternalLink,
    IconHeartStroke,
  },
  mixins: [metrics, hoursMixin],
  props: {
    storeData: {
      type: Object,
      required: true,
    },
    pageHeadingData: {
      type: Object,
      required: true,
    },
    isMyStore: {
      type: Boolean,
      required: true,
    },
    isEnabledMyStore: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    getStoreOpeningSoon() {
      return this.storeData.storeOpeningSoon;
    },
    getStoreDisplayHours() {
      return this.storeData.storeHours != null && this.pageHeadingData.inStoreAvailable
              && this.storeData.storeHours.length > 0 && !this.storeData.storeOpeningSoon;
    },
    getStoreDisplaySpecialHours() {
      return this.storeData.storeSpecialHours != null
          && this.storeData.storeSpecialHours.length > 0 && !this.storeData.storeOpeningSoon;
    },
    getNewSpecialHours() {
      return this.groupDatesInARow(this.groupSpecialHours(this.storeData.storeSpecialHours));
    },
    getStoreDisplayPhone() {
      return this.storeData.phoneNumber && !this.storeData.storeOpeningSoon;
    },
    getStoreDisplayCurbsideHours() {
      return this.storeData.storeCurbsideHours != null && this.storeData.showStoreCurbsideHours
              && this.pageHeadingData.curbsideAvailable
              && this.storeData.storeCurbsideHours.length > 0 && !this.storeData.storeOpeningSoon;
    },
    displayOpenUntil() {
      return this.pageHeadingData.showOpenUntil && !this.pageHeadingData.inStoreAvailable;
    },
  },
  mounted() {
    this.pageLoad();
  },
  methods: {
    getHoursPrinted(storeHour) {
      if (storeHour.state !== '') {
        return storeHour.state;
      }
      return `${storeHour.openAt} - ${storeHour.closeAt}`;
    },
    setAsMyRei() {
      this.metricsClick(`Set as my REI_${this.storeData.storeDisplayName}`);
      this.$emit('my-store-selected', this.storeData.storeNumber);
    },
  },
};
</script>
<template>
  <div class="store-info-column">
    <!-- Store Hours -->
    <template v-if="getStoreDisplayHours">
      <div class="store-data__display-hours">
        <cdr-text
          tag="h2"
          data-ui="store-hours"
          class="store-data__title store-data__title-margin"
        >
          Store hours
        </cdr-text>
        <!-- hours table -->

        <cdr-list
          class="store-data__list store-info-list"
          data-ui="store-days"
        >
          <li
            v-for="hour in storeData.storeHours"
            :key="hour.days"
          >
            <cdr-text
              tag="strong"
              class="store-data__days"
            >
              {{ hour.days }}
            </cdr-text>
            <span class="store-data__formated-hours">
              &nbsp; {{ getHoursPrinted(hour) }}
            </span>
          </li>
        </cdr-list>
      </div>
    </template>

    <!-- Special Store Hours -->
    <template
      v-if="getStoreDisplaySpecialHours"
    >
      <div
        v-for="specialHour in getNewSpecialHours"
        :key="specialHour.title"
        class="store-data__special-hours"
      >
        <cdr-text
          data-ui="special-hours__title"
          tag="h2"
          class="store-data__title store-data__title-margin"
        >
          {{ specialHour.title }}
        </cdr-text>
        <!-- Special hours table -->

        <cdr-list
          v-for="(item, index) in specialHour.body"
          :key="'specialHour-' + index"
          class="store-data__list store-info-list"
          data-ui="special-days"
        >
          <li class="store-data__special-hour">
            <div
              data-ui="special-hours"
              class="store-data__label"
            >
              <cdr-text
                class="store-data__date-time"
                tag="strong"
              >
                {{ item.dates }}
              </cdr-text>
              <br>
              <cdr-text
                class="store-data__date-time"
                tag="strong"
              >
                {{ item.days }}
              </cdr-text>&nbsp; {{ item.hours }}
            </div>
          </li>
        </cdr-list>
      </div>
    </template>
    <!-- Curbside Hours -->
    <template v-if="getStoreDisplayCurbsideHours">
      <cdr-text
        data-ui="store-curbside-hours"
        tag="h2"
        class="store-data__curbside-hours"
      >
        Curbside Pickup hours
      </cdr-text>
      <!-- hours table -->

      <cdr-list
        class="store-data__list store-info-list"
        data-ui="curbside-store-days"
      >
        <li
          v-for="hour in storeData.storeCurbsideHours"
          :key="hour.days"
          class="store-data__label"
        >
          <cdr-text
            tag="strong"
            class="store-data__date-time"
          >
            {{ hour.days }}
          </cdr-text>
          <span>
            &nbsp; {{ getHoursPrinted(hour) }}
          </span>
        </li>
      </cdr-list>

      <!-- learn more-->
      <div
        class="store-data__learn-more"
        data-ui="learn-more-curbside-link"
      >
        <cdr-link
          id="learn_more"
          href="/help/curbside-pickup"
          modifier="standalone"
          @click="metricsClick('Learn about curbside pickup')"
        >
          Learn about curbside pickup
        </cdr-link>
      </div>
    </template>

    <!-- Special Messages Below Hours -->
    <template
      v-if="showStoreSpecialMessageBelowHours"
    >
      <div
        v-for="specialMessage in getSpecialMessagesBelowHours()"
        :key="specialMessage.name"
        class="store-data__special-message"
      >
        <cdr-text
          tag="h2"
          data-ui="special-events-below-hours"
          class="store-info-title store-data__special-hours"
        >
          {{ specialMessage.name }}
        </cdr-text>

        <div
          class="store-data__event store-info-list"
          data-ui="store-special-event-customer-notes-below-hours"
          v-html="getCustomerNotesHtml(specialMessage)"
        />
      </div>
    </template>

    <!-- Phone -->
    <template v-if="getStoreDisplayPhone">
      <div class="store-data__phone-block">
        <cdr-text
          data-ui="contact-us"
          class="store-data__title store-data__title-margin"
          tag="h2"
        >
          Phone
        </cdr-text>
        <cdr-link
          :href="`${storeData.phoneNumberLink}`"
          class="store-data__phone-number phoneNumber"
          target="_blank"
          :aria-label="'Phone number ' + storeData.phoneNumber"
          data-analytics-id="Phone"
        >
          <span data-ui="phone-number">{{ storeData.phoneNumber }}</span>
        </cdr-link>
      </div>
    </template>

    <!-- Address -->
    <div class="addressBlock">
      <div
        class="store-data__address-list"
        data-ui="where-to-find-us"
      >
        <cdr-text
          class="store-data__title"
          tag="h2"
        >
          Address
        </cdr-text>
        <cdr-text
          v-if="storeData.address1"
          data-ui="main-address"
          class="store-data__label"
        >
          {{ storeData.address1 }}
        </cdr-text>
        <cdr-text
          v-if="storeData.address2"
          class="store-data__label"
        >
          {{ storeData.address2 }}
        </cdr-text>
        <cdr-text
          v-if="storeData.address3"
          class="store-data__label"
        >
          {{ storeData.address3 }}
        </cdr-text>
        <cdr-text
          v-if="storeData.complementaryAddress"
          data-ui="complementary-address"
          class="store-data__label"
        >
          {{ storeData.complementaryAddress }}
        </cdr-text>
      </div>

      <!-- Get Directions -->
      <div
        data-ui="get-directions-link"
        class="store-data__directions"
      >
        <cdr-link
          v-if="storeData.directionLink"
          id="get_directions"
          :href="`${storeData.directionLink}`"
          modifier="standalone"
          class="store-get-directions"
          target="_blank"
          @click="metricsClick('Get directions')"
        >
          Get directions
          <IconExternalLink
            inherit-color
            class="store-data__direction-icon"
          />
          <span class="store-data__direction-link">Opens in a new window</span>
        </cdr-link>
      </div>
    </div>

    <div
      v-if="isEnabledMyStore && !isMyStore"
      class="store-data__my-store"
    >
      <cdr-chip
        class="store-data__my-store-button"
        aria-pressed="false"
        @click="setAsMyRei"
      >
        Set as my REI
        <template #icon-left>
          <icon-heart-stroke
            inherit-color
            size="small"
          />
        </template>
      </cdr-chip>
    </div>

    <!-- Special Messages Address Notes -->
    <template v-if="showStoreSpecialMessageAddressNotes">
      <!-- notes table -->
      <div class="addressBlock">
        <cdr-list
          class="store-data__list store-info-list"
          data-ui="address-notes"
        >
          <li
            v-for="addressNotes in getSpecialMessagesAddressNotes()"
            :key="addressNotes.name"
          >
            <div
              class="store-data__label"
              v-html="getCustomerNotesHtml(addressNotes)"
            />
          </li>
        </cdr-list>
      </div>
    </template>
  </div>
</template>